import { useEffect, useState } from "react"
import * as Location from "expo-location"
import { Platform } from "react-native"
import { useQuery } from "@tanstack/react-query"
import sdk from "@/api/sdk"
import { type PropertyAutocompleteQuery } from "@/generated/graphql"
import { useDebounce } from "expo-dev-launcher/bundle/hooks/useDebounce"

interface AutoCompleteResult {
  address: string
  street: string
  stateCityZip: string
  placeId: string
}

function mapResults(results: PropertyAutocompleteQuery): AutoCompleteResult[] {
  if (!results.googlePlaces) {
    return []
  }

  return results.googlePlaces
    .map((result) => {
      if (!result?.placeId || !result.address) {
        return null
      }
      const firstIndex = result?.address?.indexOf(", ") || -1
      let street: string = result?.address || ""
      let stateCityZip: string = ""
      if (firstIndex >= 0) {
        street = result?.address?.substring(0, firstIndex) || ""
        stateCityZip = result?.address?.substring(firstIndex + 2) || ""
      }
      return {
        street,
        stateCityZip,
        address: result.address,
        placeId: result.placeId,
      }
    })
    .filter((result) => result !== null) as AutoCompleteResult[]
}

export const usePropertyAutocomplete = (
  address: string | null,
  placeId: string | null = null
) => {
  const [location, setLocation] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync()
      if (status !== "granted") {
        console.log("Permission to access location was denied")
        return
      }

      try {
        const location =
          (Platform.OS !== "web" &&
            (await Location.getLastKnownPositionAsync())) ||
          (await Location.getCurrentPositionAsync())
        setLocation(`${location.coords.latitude},${location.coords.longitude}`)
      } catch (err) {
        console.warn(err)
      }
    })()
  }, [])

  const addressInput = useDebounce(address, 500)

  return useQuery({
    queryKey: ["property-autocomplete", addressInput, location, placeId],
    queryFn: async () => {
      return addressInput && addressInput !== "" && addressInput.length > 2
        ? mapResults(
            await sdk.PropertyAutocomplete({
              address: addressInput,
              location,
              placeId,
            })
          )
        : []
    },
  })
}
